"use client";

import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import { httpBatchStreamLink, loggerLink } from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import { type inferRouterInputs, type inferRouterOutputs } from "@trpc/server";
import { useState } from "react";
import SuperJSON from "superjson";

import { type AppRouter } from "~/server/api/root";
import { createQueryClient } from "./query-client";

const IMPERSONATION_STORAGE_KEY = "dadb_impersonation";

let clientQueryClientSingleton: QueryClient | undefined = undefined;
const getQueryClient = () => {
  if (typeof window === "undefined") {
    return createQueryClient();
  }
  clientQueryClientSingleton ??= createQueryClient();

  return clientQueryClientSingleton;
};

interface StoredImpersonation {
  isImpersonating: boolean;
  impersonatedAccessToken: string | null;
  impersonatedUser: {
    orgId: number;
    organizationName: string;
    permission?: string | null;
    jurisName?: string | null;
    jurisId?: number | null;
  } | null;
}

function getStoredImpersonation(): StoredImpersonation | null {
  if (typeof window === "undefined") return null;

  try {
    const stored = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as StoredImpersonation;
      if (parsed.isImpersonating && parsed.impersonatedAccessToken) {
        return parsed;
      }
    }
  } catch {
    // Ignore parsing errors
  }
  return null;
}

export const api = createTRPCReact<AppRouter>();

export type RouterInputs = inferRouterInputs<AppRouter>;

export type RouterOutputs = inferRouterOutputs<AppRouter>;

export function TRPCReactProvider(props: { children: React.ReactNode }) {
  const queryClient = getQueryClient();

  const [trpcClient] = useState(() =>
    api.createClient({
      links: [
        loggerLink({
          enabled: (op) =>
            process.env.NODE_ENV === "development" ||
            (op.direction === "down" && op.result instanceof Error),
        }),
        httpBatchStreamLink({
          transformer: SuperJSON,
          url: getBaseUrl() + "/api/trpc",
          headers: () => {
            const headers = new Headers();
            headers.set("x-trpc-source", "nextjs-react");

            const impersonation = getStoredImpersonation();
            if (impersonation?.impersonatedAccessToken) {
              headers.set("x-impersonation-token", impersonation.impersonatedAccessToken);
              if (impersonation.impersonatedUser) {
                headers.set("x-impersonation-org-id", String(impersonation.impersonatedUser.orgId));
                headers.set("x-impersonation-org-name", impersonation.impersonatedUser.organizationName);
                if (impersonation.impersonatedUser.permission) {
                  headers.set("x-impersonation-permission", impersonation.impersonatedUser.permission);
                }
                if (impersonation.impersonatedUser.jurisName) {
                  headers.set("x-impersonation-juris-name", impersonation.impersonatedUser.jurisName);
                }
                if (impersonation.impersonatedUser.jurisId != null) {
                  headers.set("x-impersonation-juris-id", String(impersonation.impersonatedUser.jurisId));
                }
              }
            }

            return headers;
          },
        }),
      ],
    }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <api.Provider client={trpcClient} queryClient={queryClient}>
        {props.children}
      </api.Provider>
    </QueryClientProvider>
  );
}

function getBaseUrl() {
  if (typeof window !== "undefined") return window.location.origin;
  if (process.env.VERCEL_URL) return `https://${process.env.VERCEL_URL}`;
  return `http://localhost:${process.env.PORT ?? 3000}`;
}
